import * as Sentry from '@sentry/react';
import { FetchApiError } from '@/modules/fetch-core';

// Exported separately from the entry so the Sentry semantics are testable
// in isolation.
export function sendCapturedException(e: unknown, extra?: object): void {
  Sentry.withScope((scope) => {
    if (e instanceof FetchApiError) {
      scope.setTag('source', 'fetchApi');
      scope.setContext('request', serializeContext(e.extra));
    }
    if (extra) {
      scope.setContext('extra', serializeContext(extra));
    }
    Sentry.captureException(e);
  });
}

// The error-reporting entry for caught exceptions: always console.errors the
// error itself. On localhost (VITE_IS_LOCALHOST, read at call time) it
// additionally console.errors `extra` and stops — nothing reaches Sentry.
// Everywhere else the error is captured to Sentry instead, with `extra`
// attached only to the event (not the console).
//
// Sentry enrichment:
// - a @/modules/fetch-core FetchApiError (instanceof) contributes the tag
//   source=fetchApi and its `.extra` request context as the event's
//   "request" context;
// - the optional `extra` object becomes the event's "extra" context;
// - context values are serialized defensively before attaching: bigint →
//   decimal string (JSON serialization throws on bigint, nested ones
//   included), TOP-LEVEL Error values → { name, message, stack } (a NESTED
//   Error serializes to {} like any Error under JSON), objects that still
//   fail to serialize (e.g. circular) → { unserializable: constructor name,
//   null when the object has none; keys: own enumerable key names }.
export function captureException(e: unknown, extra?: object): void {
  console.error(e); // eslint-disable-line no-console -- console mirror is part of the capture contract

  if (import.meta.env.VITE_IS_LOCALHOST) {
    if (extra) console.error(extra); // eslint-disable-line no-console -- localhost surfaces extra inline instead of Sentry
    return;
  }

  sendCapturedException(e, extra);
}

function serializeContext(obj: object) {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    out[key] = sanitize(value);
  }
  return out;
}

function sanitize(value: unknown): unknown {
  if (typeof value === 'bigint') return value.toString();
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }
  if (
    typeof value === 'object' &&
    // Stryker disable next-line ConditionalExpression: equivalent — this arm diverges from `true` only at value === null (typeof null === 'object'), and JSON round-tripping null returns null, identical to the fall-through `return value`; JSON.stringify(null) never throws.
    value !== null
  ) {
    try {
      const cloned: unknown = JSON.parse(
        JSON.stringify(value, (_, v: unknown) =>
          typeof v === 'bigint' ? v.toString() : v,
        ),
      );
      return cloned;
    } catch {
      // TS types .constructor as Function, but a null-prototype object has
      // none at runtime — widen to unknown and check honestly.
      const ctor: unknown = value.constructor;
      return {
        unserializable: typeof ctor === 'function' ? ctor.name : null,
        keys: Object.keys(value),
      };
    }
  }
  return value;
}
